import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, ChevronRight, ArrowLeft, HelpCircle, Send, Bot, Map, BookOpen, Users } from 'lucide-react';
import { streamInsight } from '../../utils/streamInsight';
import { HELP_TOPICS, HELP_CATEGORIES, searchTopics, type HelpTopic } from './helpContent';
import { APP_OVERVIEW_KEY } from './AppOverviewPage';
import './HelpDrawer.css';

// ── Simple markdown renderer ──────────────────────────────────────────────────
// Handles: ## headings, **bold**, bullet lists, `code`, tables, code blocks
function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      nodes.push(
        <pre key={i} className="hd-code-block">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      i++;
      continue;
    }

    // Table row
    if (line.startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // Skip separator row (|---|---|)
      const rows = tableLines.filter((r) => !/^\|[-| :]+\|$/.test(r.trim()));
      nodes.push(
        <table key={i} className="hd-table">
          <tbody>
            {rows.map((r, ri) => {
              const cells = r.split('|').filter((_, ci) => ci > 0 && ci < r.split('|').length - 1);
              const Tag = ri === 0 ? 'th' : 'td';
              return (
                <tr key={ri}>
                  {cells.map((cell, ci) => (
                    <Tag key={ci} className="hd-table-cell">{inlineMarkdown(cell.trim())}</Tag>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      );
      continue;
    }

    // ## Heading
    if (line.startsWith('## ')) {
      nodes.push(<h3 key={i} className="hd-heading">{line.slice(3)}</h3>);
      i++;
      continue;
    }

    // Bullet list: lines that start with - or *
    if (line.match(/^[-*] /)) {
      const items: string[] = [];
      while (i < lines.length && lines[i].match(/^[-*] /)) {
        items.push(lines[i].slice(2));
        i++;
      }
      nodes.push(
        <ul key={i} className="hd-list">
          {items.map((item, idx) => (
            <li key={idx}>{inlineMarkdown(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (line.match(/^\d+\. /)) {
      const items: string[] = [];
      while (i < lines.length && lines[i].match(/^\d+\. /)) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      nodes.push(
        <ol key={i} className="hd-list hd-list-ordered">
          {items.map((item, idx) => (
            <li key={idx}>{inlineMarkdown(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Blank line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Normal paragraph
    nodes.push(<p key={i} className="hd-para">{inlineMarkdown(line)}</p>);
    i++;
  }

  return nodes;
}

/** Process inline **bold**, `code`, and plain text within a string. */
function inlineMarkdown(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.*?)\*\*|`(.*?)`/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1] !== undefined) parts.push(<strong key={match.index}>{match[1]}</strong>);
    if (match[2] !== undefined) parts.push(<code key={match.index} className="hd-inline-code">{match[2]}</code>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category: string }) {
  const colors: Record<string, string> = {
    'Getting Started':     'hd-cat-start',
    'Kairos Study':        'hd-cat-study',
    'Koinonia Groups':       'hd-cat-groups',
    'Church Management':   'hd-cat-church',
    'Account & Settings':  'hd-cat-account',
  };
  return <span className={`hd-cat-badge ${colors[category] || 'hd-cat-start'}`}>{category}</span>;
}

function TopicListItem({ topic, onClick }: { topic: HelpTopic; onClick: () => void }) {
  return (
    <button className="hd-topic-item" onClick={onClick}>
      <div className="hd-topic-item-inner">
        <CategoryBadge category={topic.category} />
        <span className="hd-topic-title">{topic.title}</span>
      </div>
      <ChevronRight className="hd-topic-chevron" />
    </button>
  );
}

function TopicView({ topic, onBack }: { topic: HelpTopic; onBack: () => void }) {
  return (
    <div className="hd-topic-view">
      <button className="hd-back-btn" onClick={onBack}>
        <ArrowLeft size={15} /> Back
      </button>
      <CategoryBadge category={topic.category} />
      <h2 className="hd-topic-view-title">{topic.title}</h2>
      <div className="hd-topic-content">
        {renderMarkdown(topic.content)}
      </div>
    </div>
  );
}

// ── Chat types ────────────────────────────────────────────────────────────────

type ChatMsg = { role: 'user' | 'assistant'; text: string; relatedTopics?: HelpTopic[] };

// ── Main drawer ───────────────────────────────────────────────────────────────

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function HelpDrawer({ open, onClose }: HelpDrawerProps) {
  const navigate = useNavigate();
  // ── Browse mode state ───────────────────────────────────────────────────
  const [mode, setMode]                 = useState<'browse' | 'chat'>('browse');
  const [query, setQuery]               = useState('');
  const [activeTopic, setActiveTopic]   = useState<HelpTopic | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // ── Chat mode state ─────────────────────────────────────────────────────
  const [chatHistory, setChatHistory]   = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput]       = useState('');
  const [chatLoading, setChatLoading]   = useState(false);
  const [chatStatus, setChatStatus]     = useState('');
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  // Focus appropriate input when drawer opens / mode changes
  useEffect(() => {
    if (!open) {
      setQuery(''); setActiveTopic(null); setActiveCategory(null);
      return;
    }
    if (mode === 'browse') setTimeout(() => searchRef.current?.focus(), 120);
    if (mode === 'chat')   setTimeout(() => chatInputRef.current?.focus(), 120);
  }, [open, mode]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory, chatStatus]);

  // ── Browse helpers ──────────────────────────────────────────────────────
  const results = useMemo(() => {
    if (query.trim()) return searchTopics(query);
    if (activeCategory) return HELP_TOPICS.filter((t) => t.category === activeCategory);
    return HELP_TOPICS;
  }, [query, activeCategory]);

  const groupedByCategory = useMemo(() => {
    if (query.trim() || activeCategory) return null;
    const map: Record<string, HelpTopic[]> = {};
    for (const cat of HELP_CATEGORIES) {
      map[cat] = HELP_TOPICS.filter((t) => t.category === cat);
    }
    return map;
  }, [query, activeCategory]);

  // ── Chat send ───────────────────────────────────────────────────────────
  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;

    const related = searchTopics(msg).slice(0, 4);
    const helpContext = related.map(t => ({ title: t.title, content: t.content }));

    setChatInput('');
    setChatStatus('');
    setChatHistory(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);

    // Placeholder for streaming assistant message
    setChatHistory(prev => [...prev, { role: 'assistant', text: '', relatedTopics: related.slice(0, 2) }]);

    await streamInsight(
      '/api/agent/chat',
      { message: msg, helpContext },
      {
        onToken: (delta) => {
          setChatHistory(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') last.text += delta;
            return next;
          });
        },
        onDone: (final) => {
          setChatHistory(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') last.text = final || last.text;
            return next;
          });
          setChatLoading(false);
          setChatStatus('');
        },
        onError: (err) => {
          setChatHistory(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') last.text = `Sorry, something went wrong. ${err.message}`;
            return next;
          });
          setChatLoading(false);
          setChatStatus('');
        },
      }
    );
  };

  const handleChatKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="hd-backdrop" onClick={onClose} aria-hidden="true" />

      {/* Drawer panel */}
      <aside className="hd-drawer" role="dialog" aria-label="Help">
        {/* Header */}
        <div className="hd-header">
          <div className="hd-header-left">
            {mode === 'chat' ? <Bot size={18} className="hd-header-icon" /> : <HelpCircle size={18} className="hd-header-icon" />}
            <span className="hd-header-title">{mode === 'chat' ? 'Ask Kairos' : 'Kairos Help'}</span>
          </div>
          <button className="hd-close-btn" onClick={onClose} aria-label="Close help">
            <X size={18} />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="hd-mode-tabs">
          <button
            className={`hd-mode-tab ${mode === 'browse' ? 'active' : ''}`}
            onClick={() => setMode('browse')}
          >
            Browse Topics
          </button>
          <button
            className={`hd-mode-tab ${mode === 'chat' ? 'active' : ''}`}
            onClick={() => setMode('chat')}
          >
            Ask AI
          </button>
        </div>

        {/* ── BROWSE MODE ──────────────────────────────────────────────── */}
        {mode === 'browse' && (
          <>
            {/* Search */}
            {!activeTopic && (
              <div className="hd-search-wrap">
                <Search size={15} className="hd-search-icon" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search topics…"
                  className="hd-search-input"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setActiveCategory(null); }}
                />
                {query && (
                  <button className="hd-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                    <X size={13} />
                  </button>
                )}
              </div>
            )}

            <div className="hd-body">
              {activeTopic ? (
                <TopicView topic={activeTopic} onBack={() => setActiveTopic(null)} />
              ) : query.trim() ? (
                <div>
                  <p className="hd-result-count">
                    {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
                  </p>
                  {results.length === 0 ? (
                    <p className="hd-empty">
                      No topics found.{' '}
                      <button className="hd-switch-to-chat" onClick={() => { setMode('chat'); setChatInput(query); }}>
                        Ask AI instead →
                      </button>
                    </p>
                  ) : (
                    results.map((t) => (
                      <TopicListItem key={t.id} topic={t} onClick={() => setActiveTopic(t)} />
                    ))
                  )}
                </div>
              ) : activeCategory ? (
                <div>
                  <button className="hd-back-btn" onClick={() => setActiveCategory(null)}>
                    <ArrowLeft size={15} /> All topics
                  </button>
                  <p className="hd-category-heading">{activeCategory}</p>
                  {results.map((t) => (
                    <TopicListItem key={t.id} topic={t} onClick={() => setActiveTopic(t)} />
                  ))}
                </div>
              ) : (
                <div>
                  <p className="hd-intro">How can we help?</p>
                  <button
                    className="hd-bigpicture-btn"
                    onClick={() => {
                      localStorage.removeItem(APP_OVERVIEW_KEY);
                      onClose();
                      navigate('/welcome');
                    }}
                  >
                    <Map size={16} />
                    <span>App Overview — see everything Kairos offers</span>
                    <ChevronRight size={14} className="hd-topic-chevron" />
                  </button>
                  <button
                    className="hd-bigpicture-btn"
                    onClick={() => { onClose(); navigate('/scripture-map'); }}
                  >
                    <BookOpen size={16} />
                    <span>Scripture Reading Map — explore all 4 paths</span>
                    <ChevronRight size={14} className="hd-topic-chevron" />
                  </button>
                  <button
                    className="hd-bigpicture-btn"
                    onClick={() => { onClose(); navigate('/groups-map'); }}
                  >
                    <Users size={16} />
                    <span>Koinonia Groups Map — see every page & who can access it</span>
                    <ChevronRight size={14} className="hd-topic-chevron" />
                  </button>
                  {groupedByCategory && Object.entries(groupedByCategory).map(([cat, topics]) => (
                    <div key={cat} className="hd-category-section">
                      <button className="hd-category-row" onClick={() => setActiveCategory(cat)}>
                        <span className="hd-category-name">{cat}</span>
                        <span className="hd-category-count">{topics.length} topics</span>
                        <ChevronRight size={14} className="hd-category-chevron" />
                      </button>
                      {topics.slice(0, 2).map((t) => (
                        <TopicListItem key={t.id} topic={t} onClick={() => setActiveTopic(t)} />
                      ))}
                      {topics.length > 2 && (
                        <button className="hd-show-more" onClick={() => setActiveCategory(cat)}>
                          +{topics.length - 2} more in {cat} →
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── CHAT MODE ────────────────────────────────────────────────── */}
        {mode === 'chat' && (
          <div className="hd-chat-wrap">
            {/* Message history */}
            <div className="hd-chat-body" ref={chatBodyRef}>
              {/* Greeting */}
              {chatHistory.length === 0 && (
                <div className="hd-chat-greeting">
                  <Bot size={28} className="hd-chat-greeting-icon" />
                  <p className="hd-chat-greeting-text">
                    Hi! I'm the Kairos assistant. Ask me anything about the app — how to join a group, import members, use the lectionary, or anything else.
                  </p>
                  <div className="hd-chat-suggestions">
                    {['How do I join a group?', 'How do I import members?', 'What is the Journey mode?'].map((s) => (
                      <button key={s} className="hd-chat-suggestion" onClick={() => { setChatInput(s); chatInputRef.current?.focus(); }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages */}
              {chatHistory.map((msg, i) => (
                <div key={i} className={`hd-chat-msg hd-chat-msg-${msg.role}`}>
                  <div className={`hd-chat-bubble hd-chat-bubble-${msg.role}`}>
                    {msg.text || (msg.role === 'assistant' && chatLoading && i === chatHistory.length - 1
                      ? <span className="hd-chat-typing"><span /><span /><span /></span>
                      : null
                    )}
                  </div>
                  {/* Related topics after assistant response */}
                  {msg.role === 'assistant' && msg.text && msg.relatedTopics && msg.relatedTopics.length > 0 && (
                    <div className="hd-chat-related">
                      <span className="hd-chat-related-label">Related:</span>
                      {msg.relatedTopics.map(t => (
                        <button key={t.id} className="hd-chat-related-link" onClick={() => { setMode('browse'); setActiveTopic(t); }}>
                          {t.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Status line (tool activity / processing) */}
              {chatStatus && (
                <p className="hd-chat-status">{chatStatus}</p>
              )}
            </div>

            {/* Input area */}
            <div className="hd-chat-input-wrap">
              <textarea
                ref={chatInputRef}
                className="hd-chat-input"
                placeholder="Ask a question… (Enter to send)"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleChatKey}
                rows={1}
                disabled={chatLoading}
              />
              <button
                className="hd-chat-send-btn"
                onClick={sendChat}
                disabled={!chatInput.trim() || chatLoading}
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="hd-footer">
          <span className="hd-footer-text">
            Kairos · Scripture-first, pastorally guided
          </span>
        </div>
      </aside>

    </>
  );
}

// ── Floating button ───────────────────────────────────────────────────────────

export function HelpButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="hd-fab"
        onClick={() => setOpen(true)}
        aria-label="Open help"
        title="Help"
      >
        <HelpCircle size={22} />
      </button>
      <HelpDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}
